import { useEffect, useRef } from 'react';

// Live-Aktualisierung: eine gemeinsame Verbindung (Server-Sent Events) für die ganze Seite.
// Der Server schickt nur „Typ + Mandant“, die Seiten laden ihre Daten dann neu.
export type LiveType =
  | 'chat'
  | 'belege'
  | 'notifications'
  | 'clients'
  | 'team'
  | 'lohn'
  | 'briefe'
  | 'anfragen'
  | 'fristen'
  | 'guv'
  | 'docs'
  | 'invoices'
  | 'abrechnung'
  | 'posteingang'
  | 'postausgang';
export interface LiveEvent {
  type: LiveType;
  clientId: string | null;
}

type Listener = (e: LiveEvent) => void;
const listeners = new Set<Listener>();
let source: EventSource | null = null;
// Wartezeit bis zum nächsten eigenen Verbindungsversuch (wächst bei jedem Fehlschlag, 2 s bis 30 s)
let retryTimer: ReturnType<typeof setTimeout> | undefined;
let retryDelay = 0;
// Die Verbindung war unterbrochen: Ereignisse können verloren gegangen sein.
let missed = false;
// Nach einem Abbruch wird erst die Sitzung geprüft; solange nicht neu verbinden.
let checking = false;
// Zählt closeLive(): Eine Prüfung, die danach zurückkommt, ändert nichts mehr.
let generation = 0;

function emit(e: LiveEvent) {
  for (const l of listeners) l(e);
}

function ensureSource() {
  if (source || checking || retryTimer !== undefined || typeof EventSource === 'undefined') return;
  const es = new EventSource('/api/events');
  source = es;
  es.onmessage = (msg) => {
    try {
      emit(JSON.parse(msg.data) as LiveEvent);
    } catch {
      // kaputte Nachricht ignorieren
    }
  };
  // Wieder verbunden nach einer Unterbrechung: alles einmal neu laden ('clients' lädt überall neu).
  es.onopen = () => {
    retryDelay = 0;
    if (missed) emit({ type: 'clients', clientId: null });
    missed = false;
  };
  // Bei Netzwerkfehlern verbindet sich der Browser selbst neu. Antwortet der Server beim Neuverbinden aber
  // mit einem Fehler, gibt der Browser die Verbindung für immer auf, und welcher Fehler es war, sieht die Seite
  // nicht. Darum erst die Sitzung prüfen: Ist sie beendet (in einem anderen Tab abgemeldet, neues Passwort),
  // geht es zur Anmeldung. Sonst (z. B. 502 vom Proxy, während die App nach einem Update neu startet)
  // selbst neu verbinden, mit wachsender Pause.
  es.onerror = () => {
    if (source !== es) return;
    missed = true;
    if (es.readyState !== EventSource.CLOSED) return;
    es.close();
    source = null;
    checking = true;
    const gen = generation;
    void sessionEnded().then((ended) => {
      if (gen !== generation) return;
      checking = false;
      if (ended) window.dispatchEvent(new CustomEvent('nt:logged-out', { detail: ended }));
      else scheduleRetry();
    });
  };
}

// Antwortet der Server auf „Wer bin ich?“ mit 401, ist die Sitzung vorbei (Ergebnis: der Code, z. B. „idle“).
// Andere Fehler (keine Verbindung, Server startet neu) zählen nicht: Dann wird es später noch einmal versucht.
async function sessionEnded(): Promise<string | null> {
  try {
    const res = await fetch('/api/auth/me', { credentials: 'same-origin' });
    if (res.status !== 401) return null;
    const data = (await res.json().catch(() => ({}))) as { code?: string };
    return data.code ?? 'unauthorized';
  } catch {
    return null;
  }
}

function scheduleRetry() {
  retryDelay = Math.min(retryDelay ? retryDelay * 2 : 2000, 30_000);
  retryTimer = setTimeout(
    () => {
      retryTimer = undefined;
      ensureSource();
    },
    // etwas Zufall, damit nach einem Neustart nicht alle offenen Seiten im selben Moment anfragen
    retryDelay + Math.random() * 1000,
  );
}

export function closeLive() {
  source?.close();
  source = null;
  clearTimeout(retryTimer);
  retryTimer = undefined;
  retryDelay = 0;
  missed = false;
  checking = false;
  generation += 1;
}

export function useLive(types: LiveType[] | null, onEvent: (e: LiveEvent) => void) {
  const cb = useRef(onEvent);
  cb.current = onEvent;
  const key = types?.join(',') ?? '';
  useEffect(() => {
    if (!types) return;
    ensureSource();
    const l: Listener = (e) => {
      // 'clients' heißt: Zuweisungen oder Verbindung haben sich geändert, alle laden neu.
      if (e.type === 'clients' || types.includes(e.type)) cb.current(e);
    };
    listeners.add(l);
    return () => {
      listeners.delete(l);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
}
